import React, { useState } from 'react'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Check } from 'lucide-react'

interface ColorPickerProps {
  color: string
  onChange: (color: string) => void
  disabled?: boolean
  label?: string
}

const predefinedColors = [
  '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
  '#fef2f2', '#fee2e2', '#fecaca', '#f87171', '#ef4444', '#dc2626', '#b91c1c',
  '#fefce8', '#fef3c7', '#fed7aa', '#fdba74', '#f59e0b', '#d97706', '#b45309',
  '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#16a34a', '#15803d',
  '#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb',
  '#f0f9ff', '#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7',
  '#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea',
  '#fdf4ff', '#fae8ff', '#f5d0fe', '#f0abfc', '#e879f9', '#d946ef', '#c026d3',
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  disabled = false,
  label
}) => {
  const [open, setOpen] = useState(false)
  const [customColor, setCustomColor] = useState(color)

  const handleColorSelect = (selectedColor: string) => {
    onChange(selectedColor)
    setCustomColor(selectedColor)
    setOpen(false)
  }

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value
    setCustomColor(newColor)
    onChange(newColor)
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className="w-full justify-start text-left font-normal"
          >
            <div className="flex items-center gap-2">
              <div
                className="h-4 w-4 rounded border border-gray-300"
                style={{ backgroundColor: color }}
              />
              <span>{color.toUpperCase()}</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Preset Colors</Label>
              <div className="grid grid-cols-7 gap-2 mt-2">
                {predefinedColors.map((presetColor) => (
                  <button
                    key={presetColor}
                    className="relative h-8 w-8 rounded border border-gray-300 hover:scale-105 transition-transform"
                    style={{ backgroundColor: presetColor }}
                    onClick={() => handleColorSelect(presetColor)}
                    disabled={disabled}
                  >
                    {color === presetColor && (
                      <Check className="h-4 w-4 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="custom-color" className="text-sm font-medium">
                Custom Color
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="custom-color"
                  type="color"
                  value={customColor}
                  onChange={handleCustomColorChange}
                  className="h-10 w-16 p-1 rounded border border-gray-300"
                  disabled={disabled}
                />
                <Input
                  placeholder="#000000"
                  value={customColor}
                  onChange={(e) => {
                    const newColor = e.target.value
                    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
                      setCustomColor(newColor)
                      onChange(newColor)
                    } else {
                      setCustomColor(newColor)
                    }
                  }}
                  className="flex-1"
                  disabled={disabled}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(customColor)) {
                    onChange(customColor)
                  }
                  setOpen(false)
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}